<template>
  <Transition name="banner">
    <div v-if="shouldShow" class="subscription-inactive-banner" role="alert">
      <div class="subscription-inactive-banner__content">
        <Icon icon="mdi:alert-circle-outline" class="subscription-inactive-banner__icon" />
        <span class="subscription-inactive-banner__message">{{ message }}</span>
      </div>
      <button type="button" class="subscription-inactive-banner__cta" @click="handleCTAClick">
        {{ ctaLabel }}
      </button>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import { useBillingStatus } from "@/composables/billing/useBillingStatus";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { usageBlockedState } = useBillingStatus();

const isOnBillingPage = computed(() => route.path.startsWith("/settings/billing"));

const hasErrorPersistentBanner = computed(() => store.getters.persistentBanner?.type === "error");

const shouldShow = computed(
  () => usageBlockedState.value.blocked && !isOnBillingPage.value && !hasErrorPersistentBanner.value
);

const message = computed(() => {
  const reason = usageBlockedState.value.reason;
  if (reason === "past_due" || reason === "unpaid") {
    return "Your last payment failed. Update your payment method to keep using calls, SMS, and AI features.";
  }
  if (reason === "canceled") {
    return "Your subscription has been canceled. Reactivate to continue using paid features.";
  }
  if (reason === "incomplete" || reason === "incomplete_expired") {
    return "Your subscription setup is incomplete. Complete it to use paid features.";
  }
  return "Your subscription is inactive. Update your billing to continue using paid features.";
});

const ctaLabel = computed(() => {
  const reason = usageBlockedState.value.reason;
  if (reason === "past_due" || reason === "unpaid") return "Update payment method";
  if (reason === "canceled") return "Reactivate";
  if (reason === "incomplete" || reason === "incomplete_expired") return "Complete setup";
  return "Go to billing";
});

const handleCTAClick = () => {
  router.push({ path: "/settings/billing" });
};
</script>

<style scoped>
.subscription-inactive-banner {
  width: 100%;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  z-index: 1000;
  color: #fff;
  background: #dc2626;
}

.subscription-inactive-banner__content {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.subscription-inactive-banner__icon {
  font-size: 18px;
  flex-shrink: 0;
}

.subscription-inactive-banner__message {
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subscription-inactive-banner__cta {
  flex-shrink: 0;
  padding: 4px 10px;
  min-height: 28px;
  background: #fff;
  color: #dc2626;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.subscription-inactive-banner__cta:hover {
  background: #f8f8ff;
}

.subscription-inactive-banner__cta:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.banner-enter-active,
.banner-leave-active {
  transition: all 0.3s ease;
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
